/**
 * The finance figures the platform does not hold, entered once and shared by
 * every executive screen that reads money: the period, the revenue lines,
 * the operating cost by area, the capital lines and the passengers.
 */
import { useState } from "react";
import { demoFinance, financeOrigin, type Finance } from "../../domain/finance";
import { Button, Card, Field, Input } from "../../design/components";
import { useEntered } from "./store";
import { t } from "../../i18n";
import s from "./Executive.module.css";

export const FINANCE_TOPIC = "executive.finance";

export function useFinance() {
  return useEntered<Finance>(FINANCE_TOPIC, demoFinance());
}

export function FinanceForm({ finance, onSave }: { finance: Finance; onSave: (f: Finance) => void }) {
  const [f, setF] = useState<Finance>(finance);
  const num = (v: string) => (v === "" ? 0 : Number(v));
  const setLine = (i: number, k: "actual" | "budget", v: string) =>
    setF({ ...f, revenue: f.revenue.map((l, j) => (j === i ? { ...l, [k]: num(v) } : l)) });
  const setArea = (i: number, k: "actual" | "budget", v: string) =>
    setF({ ...f, opexByArea: (f.opexByArea ?? []).map((l, j) => (j === i ? { ...l, [k]: num(v) } : l)) });
  return (
    <Card
      title="The finance figures"
      sub={
        finance.basis === "demo"
          ? "the demonstration's, until the finance system's are connected or entered here — every tile that rests on them says so"
          : financeOrigin(finance)
      }
    >
      <form
        className={s.formWide}
        onSubmit={(e) => {
          e.preventDefault();
          onSave({
            ...f,
            opex: {
              ...f.opex,
              actual: (f.opexByArea ?? []).reduce((t, a) => t + a.actual, 0) || f.opex.actual,
              budget: (f.opexByArea ?? []).reduce((t, a) => t + a.budget, 0) || f.opex.budget,
            },
            basis: "entered",
          });
        }}
      >
        <div className={s.form}>
          <Field label="period">
            <Input
              value={f.period}
              onChange={(e) => setF({ ...f, period: e.target.value })}
              aria-label={t("finance period")}
            />
          </Field>
          <Field label="days it covers">
            <Input
              type="number"
              min={1}
              value={f.daysElapsed}
              onChange={(e) => setF({ ...f, daysElapsed: num(e.target.value) || 1 })}
              aria-label={t("days covered")}
            />
          </Field>
          <Field label="passengers in the period">
            <Input
              type="number"
              min={0}
              value={f.passengers ?? ""}
              onChange={(e) => setF({ ...f, passengers: e.target.value ? num(e.target.value) : undefined })}
              aria-label={t("passengers in the period")}
            />
          </Field>
          <Field label="currency">
            <Input
              value={f.currency}
              onChange={(e) => setF({ ...f, currency: e.target.value || "SAR" })}
              aria-label={t("currency")}
            />
          </Field>
        </div>
        <div className={s.colTitle}>{t("Revenue lines — actual and plan for the period")}</div>
        <div className={s.form}>
          {f.revenue.map((l, i) => (
            <Field key={l.key} label={l.label}>
              <div style={{ display: "grid", gap: 4 }}>
                <Input
                  type="number"
                  min={0}
                  value={l.actual}
                  onChange={(e) => setLine(i, "actual", e.target.value)}
                  aria-label={t("{label} revenue actual", { label: t(l.label) })}
                />
                <Input
                  type="number"
                  min={0}
                  value={l.budget}
                  onChange={(e) => setLine(i, "budget", e.target.value)}
                  aria-label={t("{label} revenue plan", { label: t(l.label) })}
                />
              </div>
            </Field>
          ))}
        </div>
        <div className={s.colTitle}>{t("Operating cost by area — actual and budget")}</div>
        <div className={s.form}>
          {(f.opexByArea ?? []).map((l, i) => (
            <Field key={l.key} label={l.label}>
              <div style={{ display: "grid", gap: 4 }}>
                <Input
                  type="number"
                  min={0}
                  value={l.actual}
                  onChange={(e) => setArea(i, "actual", e.target.value)}
                  aria-label={t("{label} cost actual", { label: t(l.label) })}
                />
                <Input
                  type="number"
                  min={0}
                  value={l.budget}
                  onChange={(e) => setArea(i, "budget", e.target.value)}
                  aria-label={t("{label} cost budget", { label: t(l.label) })}
                />
              </div>
            </Field>
          ))}
        </div>
        <div className={s.row}>
          <Button type="submit" variant="primary">
            {t("Save the finance figures")}
          </Button>
          <span className={s.muted}>
            {t(
              "Saved on the platform as a dated note under your name; capital lines are edited on the Capital Projects screen.",
            )}
          </span>
        </div>
      </form>
    </Card>
  );
}
